import {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {
    getDuplicatedItemData,
    isNew,
    useDuplicateFormId,
} from 'services/utils/editForm'
import {useGetGuidesQuery} from 'store/slices/api/guidesApi'
import {
    useCreatePublicationMutation,
    useGetPublicationQuery,
    useSavePublicationMutation,
} from 'store/slices/api/publicationsApi'
import {useUploadImageMutation} from 'store/slices/api/uploadsApi'
import {useI18n} from 'store/Store'
import {useAlert} from 'components/Display/Alert/AlertContext'
import {useNavigate} from 'react-router-dom'
import {yupResolver} from '@hookform/resolvers/yup'
import {publicationSchema} from 'services/validations/schemas/publication'
import dayjs from 'services/utils/dayjs'
import {useGetPublicationFoldersQuery} from 'store/slices/api/publicationFoldersApi'

const usePublicationForm = (publicationId) => {
    const {i18n} = useI18n()
    const {showAlert} = useAlert()
    const navigate = useNavigate()
    const isNewPublication = isNew(publicationId)
    const duplicateFromId = useDuplicateFormId(publicationId)
    const formPublicationId = isNewPublication ? duplicateFromId : publicationId

    const [doUploadImage] = useUploadImageMutation()

    const [formError, setFormError] = useState(null)
    const [guideSearchValue, setGuideSearchValue] = useState()
    const [searchPublicationFolderValue, setSearchPublicationFolderValue] =
        useState()

    const [doCreatePublication] = useCreatePublicationMutation()
    const [doSavePublication] = useSavePublicationMutation()
    const {data: guides} = useGetGuidesQuery(
        {search: guideSearchValue},
        {skip: !guideSearchValue}
    )
    const {data: publicationFoldersResults} = useGetPublicationFoldersQuery(
        {search: searchPublicationFolderValue}
        // {skip: !searchPublicationFolderValue}
    )
    const {data: publication} = useGetPublicationQuery(formPublicationId, {
        skip: !formPublicationId,
    })

    const form = useForm({
        defaultValues: {isActive: true},
        mode: 'onSubmit',
        resolver: yupResolver(publicationSchema),
    })
    const formValues = form.watch()

    useEffect(() => {
        if (publication) {
            form.reset(
                isNewPublication
                    ? getDuplicatedItemData(publication)
                    : publication
            )
        }
    }, [publication, isNewPublication, form])

    const onSubmit = async (values) => {
        let response
        const body = {
            ...values,
            guide: values.guide?._id,
            date: dayjs(formValues.date).format('YYYY-MM-DD'),
        }
        if (values._id) {
            response = await doSavePublication(body)
        } else {
            response = await doCreatePublication(body)
        }

        if (response?.data?._id) {
            const message = i18n.t('form.savedSuccessfully', {
                itemName: i18n.t('tables.publication'),
            })
            navigate(`/publications/${response.data._id}`)
            showAlert(message)
        }
        if (response.error) {
            setFormError(response.error.data.error)
        }
    }

    const submit = form.handleSubmit(onSubmit)

    const uploadFlyer = async (selectedFile) => {
        const formData = new FormData()
        const fileToUpload =
            selectedFile instanceof File
                ? selectedFile
                : form.getValues('flierFile')
        formData.append('files', fileToUpload)
        formData.append('folderName', 'publications_flayers')

        const response = await doUploadImage({
            formData,
            folderName: 'publications_flayers',
        })

        const link = response?.data?.files?.[0].link

        if (link) {
            const url = link.split('/')[2]
            form.setValue('flierUrl', url)
        }
    }

    return {
        form,
        formError,
        publication,
        submit,
        guides,
        guideSearchValue,
        setGuideSearchValue,
        publicationFoldersResults,
        searchPublicationFolderValue,
        setSearchPublicationFolderValue,
        uploadFlyer,
    }
}

export default usePublicationForm
